import React, { useState } from 'react';
import { ACCENT, ACCENT_LIGHT, TEXT_DARK, TEXT_MID, FONT } from '../lib/tokens';
import { Ic } from '../components/Icons';
import {
  Card,
  Field,
  ScreenWrap,
  SectionLabel,
  SubmitBtn,
} from '../components/ui/Primitives';
import { useRealtimeTable } from '../lib/useRealtimeTable';
import { supabase, hasSupabase } from '../lib/supabase';
import { useBreakpoint } from '../lib/useBreakpoint';

const FALLBACK = [
  {
    id: 'demo-1',
    name: 'Sarah Mitchell',
    commitment: 'Implement an AI-assisted member onboarding flow by Q3 2026.',
    by_when: '2026-09-01',
    created_at: '2026-05-22T14:52:00-07:00',
  },
  {
    id: 'demo-2',
    name: 'James Okafor',
    commitment:
      'Draft a succession planning policy and present it to my board at the August meeting.',
    by_when: '2026-08-15',
    created_at: '2026-05-22T14:49:00-07:00',
  },
];

export default function CommitmentsScreen() {
  const { rows, setRows } = useRealtimeTable('commitments');
  const commitments = rows.length ? rows : hasSupabase ? [] : FALLBACK;

  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [byWhen, setByWhen] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    if (!name.trim() || !text.trim() || !byWhen) return;
    const payload = {
      name: name.trim(),
      commitment: text.trim(),
      by_when: byWhen,
    };
    setName('');
    setText('');
    setByWhen('');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 2500);

    if (hasSupabase) {
      // Realtime brings the canonical row back; don't prepend optimistically.
      await supabase.from('commitments').insert(payload);
    } else {
      setRows((prev) => [
        { id: `local-${Date.now()}`, ...payload, created_at: new Date().toISOString() },
        ...prev,
      ]);
    }
  };

  const bp = useBreakpoint();

  const formCard = (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_DARK }}>
        Record Your Commitment
      </div>
        <Field label="Your Name" value={name} onChange={setName} placeholder="e.g. Sarah Mitchell" />
        <Field
          label="My Commitment"
          value={text}
          onChange={setText}
          placeholder="I commit to…"
          multiline
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label
            style={{ fontSize: 11.5, fontWeight: 600, color: TEXT_MID, letterSpacing: '0.04em' }}
          >
            By When
          </label>
          <input
            type="date"
            value={byWhen}
            onChange={(e) => setByWhen(e.target.value)}
            style={{
              border: '1.5px solid #e2e8f0',
              borderRadius: 8,
              padding: '9px 11px',
              fontSize: 13.5,
              color: TEXT_DARK,
              fontFamily: FONT,
              outline: 'none',
              background: '#fff',
            }}
          />
        </div>
        {submitted ? (
          <div
            style={{
              background: '#dcfce7',
              border: '1px solid #86efac',
              borderRadius: 9,
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Ic.Check size={16} color="#16a34a" />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>
              Commitment recorded!
            </span>
          </div>
        ) : (
          <SubmitBtn onClick={submit} disabled={!name.trim() || !text.trim() || !byWhen}>
            Submit Commitment
          </SubmitBtn>
        )}
    </Card>
  );

  const board = (
    <>
      <SectionLabel>
        COMMITMENT BOARD · {commitments.length} RECORDED
      </SectionLabel>
      {commitments.map((c) => (
        <Card key={c.id} style={{ padding: '13px 14px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 7,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: ACCENT_LIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>
                  {c.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>{c.name}</div>
            </div>
            <div
              style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 20,
                padding: '2px 9px',
              }}
            >
              <span style={{ fontSize: 10.5, fontWeight: 600, color: '#16a34a' }}>
                Due {formatDate(c.by_when)}
              </span>
            </div>
          </div>
          <p style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.55, margin: 0 }}>
            {c.commitment}
          </p>
        </Card>
      ))}
    </>
  );

  // ── Desktop: form sticky on left, board scrollable on right ────────────
  if (bp === 'desktop') {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '380px 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div style={{ position: 'sticky', top: 0 }}>{formCard}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{board}</div>
      </div>
    );
  }

  // ── Mobile / tablet: original single-column ────────────────────────────
  return (
    <ScreenWrap>
      {formCard}
      {board}
    </ScreenWrap>
  );
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return d;
  }
}
